import React, { useEffect } from 'react'
import {
  Button,
  Container,
  Box,
  Text,
  GridItem,
  useToast,
  Heading,
  Stack,
  Divider
} from '@chakra-ui/react'
import { Navigate } from 'react-router-dom';
import { LayoutGrid } from '@blueprinthq/joy'
import { StickyFooter } from '@components'
import { useParams, useNavigate } from 'react-router-dom'

import { usePatientProfileStore } from '@core/store'

import {
  CheckInContentQuestionDto,
  CheckInContentDto,
  CheckInDtoV1,
} from '~/api/client/models'
import { 
  useUsersControllerV1GetAssignedCheckInById,
  useUsersControllerV1PostCheckInsSubmit
 } from '~/api/client'
import { useOnNavBack, useLocalStorage, useWebViewStore } from '@hooks'
import { useAppBarStore } from '@core/store'
import { BinaryTable, FreeText, LikertScale, ConfirmResetDialog } from './components'
import { LifestyleCorrelates } from './lifestyle-correlates'
import { AnswersType } from './types/answers'


interface CheckInProps {
  checkIn: CheckInDtoV1
  onChange: (key: string, value: string) => void
  answers: AnswersType
  onSubmit: () => void
  isSubmitting: boolean
}

const CheckIn = ({
  checkIn,
  onChange,
  answers,
  onSubmit,
  isSubmitting
}: CheckInProps) => {
  const navigate = useNavigate()

  useOnNavBack(() => {
    navigate(-1)
  })

  const renderLikertContent = (content: CheckInContentDto) => {
    return (
      <Box>
        <Text fontSize="lg" mb={3}>
          {content.title}
        </Text>
        {content.questions.map((question: CheckInContentQuestionDto) => {
          const options = question.answers.map(a => ({
            value: a.value.toString(),
            display: a.display
          }))
          const value = answers[question.key] || ''
          return (
            <LikertScale
              key={question.key}
              label={question.display}
              options={options}
              value={value}
              onChange={(value: string) => onChange(question.key, value)}
            />
          )
        })}
      </Box>
    )
  }

  const renderFreeTextContent = (content: CheckInContentDto, autoFocus: boolean) => {
    return (
      <Box>
        <Text mb={3}>{content.title}</Text>
        {content.questions.map((question: CheckInContentQuestionDto) => {
          const value = answers[question.key] || ''
          return (
            <FreeText
              autoFocus={autoFocus}
              key={`freetext-${question.key}`}
              value={value}
              onChange={value => onChange(question.key, value.trimLeft())}
            />
          )
        })}
      </Box>
    )
  }

  const renderBinaryTableContent = (content: CheckInContentDto) => {
    return (
      <Box>
        <Text mb={3}>{content.title}</Text>
        <Stack
          direction="column"
          spacing="0px"
          border="1px solid"
          borderColor="pale_gray"
          borderRadius="4px"
          divider={<Divider />}
        >
        {content.questions.map((question: CheckInContentQuestionDto) => {
          const value = answers[question.key] || ''
          return (
            <Box>
              <BinaryTable
                label={question.display}
                isChecked={value === '1' ? true : false}
                onChange={value => onChange(question.key, value ? '1' : '0')}
              />
            </Box>
          )
        })}
        </Stack>
      </Box>
    )
  }

  const renderContent = (content: CheckInContentDto, isFirstQuestion: boolean) => {
    switch (content.type) {
      case 'likert':
        return renderLikertContent(content)
      case 'free_text':
        return renderFreeTextContent(content, isFirstQuestion)
      case 'binary_table':
        return renderBinaryTableContent(content)
      default:
        return null
    }
  }

  const isValid = Object.keys(answers).some(k => answers[k])

  return (
    <Box pb="100px">
      <Box>
        <Heading size="lg" mb={4}>
          {checkIn.title}
        </Heading>
      </Box>
      {checkIn.content.map((content, idx) => (
        <Box key={idx} w="100%">
          <Box mb={6}>{renderContent(content, idx === 0)}</Box>
        </Box>
      ))}
      <StickyFooter>
        <Button
          size="lg"
          w="100%"
          onClick={onSubmit}
          disabled={!isValid || isSubmitting}
          title={!isValid ? 'Please answer at least one question' : ''}
        >
          Submit
        </Button>
      </StickyFooter>
    </Box>
  )
}

export const CheckInView = () => {
  const { checkInId } = useParams()
  const { activeProfile } = usePatientProfileStore()

  const appBarStore = useAppBarStore()
  const webViewStore = useWebViewStore()

  const [answers, setAnswers] = useLocalStorage<AnswersType>(
    `checkIn.${checkInId}`,
    {}
  )

  const toast = useToast()

  const navigate = useNavigate()

  const handleReset = () => {
    setAnswers({})
  }

  useEffect(() => {
    appBarStore.setRightComponent(
      <ConfirmResetDialog onReset={handleReset} />
    )

    return () => appBarStore.setRightComponent(null)
  })

  const {
    data: checkIn, isError: checkInIsError, error: checkInError 
  } = useUsersControllerV1GetAssignedCheckInById(activeProfile.userId, checkInId!, { 
    query: { enabled: !!checkInId }
  })

  const { mutate: submitCheckIn, isLoading: isSubmitting } =
    useUsersControllerV1PostCheckInsSubmit({
      mutation: {
        onSuccess() {
          navigate('/check-ins')
          setAnswers({})
          toast({
            title: 'Thanks for checking in!',
            status: 'success',
            duration: 2000,
            isClosable: true
          })
        }
      }
    })

  const handleAnswerChange = (questionKey: string, answerValue: string) => {
    setAnswers({
      ...answers,
      [questionKey]: answerValue
    })
  }

  const handleSubmit = () => {
    if (checkIn) {
      submitCheckIn({
        userId: activeProfile.userId,
        data: [
          {
            moduleId: checkIn.id,
            answers: Object.keys(answers).map(key => ({
              key,
              value: answers[key]
            }))
          }
        ]
      })
    }
  }

  if(checkInIsError && checkInError.response && checkInError.response.status === 404) {
    return <Navigate to="/check-ins" replace={true} />
  }

  if (!checkIn) return null

  return (
    <Container
      height="100%"
      paddingLeft={{
        base: '8px',
        sm: '0px'
      }}
      paddingRight={{
        base: '8px',
        sm: '0px'
      }}
    >
      <LayoutGrid>
        <GridItem
          colStart={{
            base: 1,
            sm: 2,
            md: 4
          }}
          colEnd={{
            base: 5,
            sm: 8,
            md: 10
          }}
        >
          <Box w="100%" height="100%" pt="medium" sx={{
            // Fix issue with Safari mobile browser where the web URL bar expands over the content of the page
            '@supports (-webkit-touch-callout: none)': {
              paddingBottom: webViewStore.isReactNativeWebView ? '0px' : '70px',
            }
          }}>
            {checkIn.title === 'Lifestyle correlates' ? (
              <LifestyleCorrelates
                checkIn={checkIn}
                onChange={handleAnswerChange}
                answers={answers}
                onSubmit={handleSubmit}
              />
            ) : (
              <CheckIn
                checkIn={checkIn}
                onChange={handleAnswerChange}
                answers={answers}
                onSubmit={handleSubmit}
                isSubmitting={isSubmitting}
              />
            )}
          </Box>
        </GridItem>
      </LayoutGrid>
    </Container>
  )
}
